import React from 'react';
import { View, Text } from '@react-pdf/renderer';
import { TableState } from '../../utils';
import styles from '../../styles';

type ContinuedTextIndicatorProps = {
  rowIndex: number;
  continuedText: string;
  tableState: TableState;
  hasNextRow: boolean;
};

/**
 * ContinuedTextIndicator displays a continuation indicator at the bottom of a page
 * when table rows span across multiple pages. It only shows when the current row
 * is the last row on its page and the next row appears on a different page.
 */
const ContinuedTextIndicator: React.FC<ContinuedTextIndicatorProps> = ({
  rowIndex,
  continuedText,
  tableState,
  hasNextRow
}) => {
  /**
   * Determines if the continued text should be displayed.
   * Rule: Only show if this row is the last on its page AND the next page has rows.
   *
   * @param currentPage - The actual page number from the render prop. We rely on
   * the live page rather than rowToPageMap[rowIndex] (mirroring HeadingRows),
   * because the indicator sits on the same page as its row and the live value is
   * always accurate for the current pass.
   * @returns true if continued text should be shown, false otherwise
   */
  const shouldShowContinuedText = (currentPage: number): boolean => {
    // If there's no next row, don't show continued text (table ends here)
    if (!hasNextRow) {
      return false;
    }

    const { rowToPageMap } = tableState;
    const nextPage = rowToPageMap[rowIndex + 1];

    // If the next row hasn't been placed yet, we can't tell — don't show
    if (nextPage === undefined) {
      return false;
    }

    // Show only if the next row is on a different (higher) page
    // This means current row is the last on its page and table continues
    return nextPage > currentPage;
  };

  return (
    <View
      style={{ height: 0 }}
      render={({ pageNumber }) => {
        if (!shouldShowContinuedText(pageNumber)) {
          return null;
        }

        return (
          <View style={styles.continuedTextWrapper}>
            <View style={styles.borderLine} />
            <Text style={styles.continuedText}>{continuedText}</Text>
          </View>
        );
      }}
    />
  );
};

export default ContinuedTextIndicator;
